import { Document, StyleSheet, View } from '@react-pdf/renderer';

import { Page } from '../../../components/PdfExportV2/Page/Page.tsx';
import { Text } from '../../../components/PdfExportV2/Text/Text.tsx';
import { selectAllCommentaryData } from '../../../redux/features/selectors/commentarySelectors/selectAllCommentaryData.ts';
import { COMMENTARY_TEXT, CRAFTSMAN_QUALITY_FOOTNOTE } from '../consts.ts';
import { ExportAdditionalStructures } from './ExportAdditionalStructures.tsx';
import { ExportCommentarySection } from './ExportCommentarySection.tsx';
import { ExportCommentarySubsection } from './ExportCommentarySubsection.tsx';
import { ExportCommentaryTable } from './ExportCommentaryTable.tsx';
import { ExportCostBreakdownRow } from './ExportCostBreakdownRow.tsx';

const styles = StyleSheet.create({
  costBreakdownContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  mainDwellingRoundingContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4pt',
  },
});

type Data = Pick<
  ReturnType<typeof selectAllCommentaryData>,
  | 'mainDwelling'
  | 'quality'
  | 'condition'
  | 'functionalDepreciation'
  | 'externalDepreciation'
  | 'additionalStructures'
  | 'subjectAddressDisplay'
>;

export const ExportDepreciatedCost = ({
  data: {
    mainDwelling,
    quality,
    condition,
    functionalDepreciation,
    externalDepreciation,
    additionalStructures,
    subjectAddressDisplay,
  },
  hasExternalDepreciation,
  hasFunctionalDepreciation,
}: {
  data: Data;
  hasExternalDepreciation: boolean;
  hasFunctionalDepreciation: boolean;
}) => (
  <Document>
    <Page title="">
      <ExportCommentarySection
        subjectAddress={subjectAddressDisplay.full}
        {...COMMENTARY_TEXT['dep-cost']}
      >
        <ExportCommentarySubsection {...COMMENTARY_TEXT['dep-cost'].subItems['main-dwelling']}>
          <View style={styles.costBreakdownContainer} wrap>
            <ExportCostBreakdownRow {...mainDwelling?.gla} />
            <ExportCostBreakdownRow {...mainDwelling?.fullBath} />
            <ExportCostBreakdownRow {...mainDwelling?.halfBath} />
            {mainDwelling?.basementTotal && (
              <ExportCostBreakdownRow {...mainDwelling?.basementTotal} />
            )}
            {mainDwelling?.basementFinished && (
              <ExportCostBreakdownRow {...mainDwelling?.basementFinished} />
            )}
            {mainDwelling?.roundingAndThresholds.length > 0 && (
              <View style={styles.mainDwellingRoundingContainer} wrap>
                <Text variant="h5">Rounding & Thresholds</Text>
                {mainDwelling?.roundingAndThresholds.map((item) => (
                  <Text key={item} variant="body2">
                    {item}
                  </Text>
                ))}
              </View>
            )}
          </View>
        </ExportCommentarySubsection>
        <ExportCommentarySubsection {...COMMENTARY_TEXT['dep-cost'].subItems.quality} wrap={false}>
          <ExportCommentaryTable {...quality} configKey="quality" />
          <Text variant="tableHelperText" marginTop="0px">
            {CRAFTSMAN_QUALITY_FOOTNOTE}
          </Text>
        </ExportCommentarySubsection>
        <ExportCommentarySubsection
          {...COMMENTARY_TEXT['dep-cost'].subItems.condition}
          wrap={false}
        >
          <ExportCommentaryTable {...condition} configKey="condition" />
        </ExportCommentarySubsection>
        {hasFunctionalDepreciation && (
          <ExportCommentarySubsection
            {...COMMENTARY_TEXT['dep-cost'].subItems.functionalDepreciation}
            wrap={false}
          >
            <ExportCommentaryTable {...functionalDepreciation} configKey="functionalDepreciation" />
          </ExportCommentarySubsection>
        )}
        {hasExternalDepreciation && (
          <ExportCommentarySubsection
            {...COMMENTARY_TEXT['dep-cost'].subItems.externalDepreciation}
            wrap={false}
          >
            <ExportCommentaryTable {...externalDepreciation} configKey="externalDepreciation" />
          </ExportCommentarySubsection>
        )}
        <ExportAdditionalStructures data={{ additionalStructures }} />
      </ExportCommentarySection>
    </Page>
  </Document>
);
